// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import React, { useState, useCallback, useRef, useEffect } from "react";
import * as R from "ramda";
import { connect } from "react-redux";
import { useDebouncedCallback } from "use-debounce";

import type {
  State,
  ReactRef,
  UID,
  EmailRecipients,
  UsersById,
} from "src/types";
import { searchUsers } from "src/actions";
import { addRecipient, removeRecipient } from "src/actions/email";
import {
  getUserSearchResult,
  getUserIdsByEmail,
  getUsersWithRoles,
  getRolesWithComponentPermission,
} from "src/reducers";
import useRect from "src/hooks/useRect";

import { VStack, HStack, Text, Input, Box } from "@chakra-ui/react";
import User from "src/components/chatroom/Members/User";
import SelectedUser from "src/components/chatroom/Members/SelectedUser";
import SelectedEmail from "src/components/chatroom/Members/SelectedEmail";
import { AddButton } from "src/components/chatroom/Members/styles";
import OutsideClickHandler from "src/components/OutsideClickHandler";

import * as styles from "../styles";

type Props = {
  label: string,
  recipientType: "to" | "cc" | "bcc",
  recipientData: EmailRecipients,
  results: UID[],
  extraInvitees: UsersById,
  userIdsByEmail: { [string]: [string] },
  searchUsers: Function,
  addRecipient: Function,
  removeRecipient: Function,
};

function Recipients({
  label,
  recipientType,
  recipientData,
  results,
  extraInvitees,
  userIdsByEmail,
  searchUsers,
  addRecipient,
  removeRecipient,
}: Props) {
  const frameRef: ReactRef = useRef(null);
  const dropdownRef: ReactRef = useRef(null);
  const searchRef: ReactRef = useRef(null);

  const frameBounds = useRect(frameRef, [recipientData.length]);
  const searchBounds = useRect(searchRef, [recipientData.length]);

  const addUserFramePosition = {
    top: `${frameBounds.height + 5}px`,
    left: `${searchBounds.left - frameBounds.left}px`,
  };

  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  const [debouncedSearchUsers] = useDebouncedCallback(searchUsers, 400);

  const filteredResults = R.filter(
    (result) => !R.includes(result, recipientData || []),
    results,
  );

  const handleSearch = useCallback((e) => {
    setSearch(e.target.value);
    debouncedSearchUsers(e.target.value);
  }, []);

  const handleFocus = () => setShowSearch(true);

  const resetSearch = useCallback(() => {
    setSearch("");
    searchUsers("");
  }, []);

  // reset search everytime you reopen it
  useEffect(() => {
    resetSearch();

    return () => {
      resetSearch();
    };
  }, []);

  const selectUser = useCallback((user: UID | string, email = false) => {
    addRecipient({
      prop: recipientType,
      value: !email
        ? extraInvitees[user]
          ? {
              email: extraInvitees[user].email,
              role: extraInvitees[user].orgRole,
            }
          : user
        : userIdsByEmail[user] || { email: user },
    });

    resetSearch();
    searchRef.current.focus();
  }, []);

  const unselectUser = useCallback((user: UID | string, email = false) => {
    removeRecipient({
      prop: recipientType,
      value: email ? { email: user } : user,
    });
  }, []);

  const unselectLastUser = () => {
    removeRecipient({
      prop: recipientType,
      value: R.last(recipientData),
    });
  };

  const hideSearchResults = useCallback((e: any) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target))
      setShowSearch(false);
  }, []);

  const handleKeyDown = (e) => {
    // Backspace
    if (R.isEmpty(search) && e.key === "Backspace") {
      unselectLastUser();
    }

    // Enter
    if (e.key === "Enter") {
      search.length > 0
        ? selectUser(search, true)
        : filteredResults.length > 0 && selectUser(filteredResults[0]);
    }

    // Esc
    if (e.key === "Escape" || e.key === "Esc") {
      e.preventDefault();
      e.stopPropagation();

      resetSearch();
      setShowSearch(false);
      searchRef.current.blur();
    }
  };

  return (
    <HStack width="100%">
      <Text {...styles.formLabel} alignSelf="start">
        {label}
      </Text>
      <OutsideClickHandler onClickOutside={hideSearchResults}>
        <Box cursor="text" position="relative" width="100%">
          <Box
            ref={frameRef}
            maxHeight="50px"
            overflowY="auto"
            px={2}
            onClick={() => searchRef.current.focus()}
          >
            {recipientData.map((user) =>
              typeof user === "object" ? (
                <SelectedEmail
                  key={user.email}
                  email={user.email}
                  removeSelectedUser={() => unselectUser(user.email, true)}
                />
              ) : (
                <SelectedUser
                  key={user}
                  uid={user}
                  removeSelectedUser={unselectUser}
                />
              ),
            )}

            <Input
              value={search}
              onChange={handleSearch}
              autoFocus
              onFocus={handleFocus}
              onKeyDown={handleKeyDown}
              ref={searchRef}
              {...styles.subjectInput}
              width="auto"
            />

            {showSearch && (
              <Box
                ref={dropdownRef}
                {...addUserFramePosition}
                {...styles.searchResultFrame}
              >
                {filteredResults.length > 0 ? (
                  <VStack {...styles.usersDropdown}>
                    {filteredResults.map((user) => (
                      <User
                        key={user}
                        uid={user}
                        addUserMenu
                        handleClick={() => selectUser(user)}
                        style={{ margin: "0.25rem 0" }}
                      />
                    ))}
                  </VStack>
                ) : (
                  search.length > 0 && (
                    <AddButton onClick={() => selectUser(search, true)}>
                      <Text {...styles.addButtonText}>
                        {i18n.t(k.ADD1)} {search}
                      </Text>
                    </AddButton>
                  )
                )}
              </Box>
            )}
          </Box>
        </Box>
      </OutsideClickHandler>
    </HStack>
  );
}

const mapStateToProps = ({ app }: State, { recipientType }: Props) => ({
  results: getUserSearchResult(app),
  recipientData: app.email.data[recipientType],
  userIdsByEmail: getUserIdsByEmail(app),
  extraInvitees: getUsersWithRoles(app, [
    ...getRolesWithComponentPermission(app, "prompt-invite"),
    "contact",
  ]),
});

const mapDispatchToProps = {
  addRecipient,
  removeRecipient,
  searchUsers,
};

export default connect(mapStateToProps, mapDispatchToProps)(Recipients);
